import classNames from 'classnames';
import { useId, useState } from 'react';

type IndexSet = Set<number>;

export type AccordionProps<TGroup, TItem> = {
	/** Datos de los grupos */
	readonly groups: TGroup[];

	/** Obtiene el título visible del grupo */
	readonly getGroupTitle: (group: TGroup, index: number) => React.ReactNode;

	/** Obtiene los items de cada grupo */
	readonly getItems: (group: TGroup, index: number) => TItem[];

	/** Cómo renderizar cada item */
	readonly renderItem: (item: TItem, itemIndex: number, group: TGroup, groupIndex: number) => React.ReactNode;

	/** Clave estable para el grupo (mejor que usar el índice) */
	readonly getGroupKey?: (group: TGroup, index: number) => React.Key;

	/** Clave estable para el item (mejor que usar el índice) */
	readonly getItemKey?: (item: TItem, itemIndex: number, group: TGroup, groupIndex: number) => React.Key;

	/** Varias secciones abiertas a la vez */
	readonly isMultiple?: boolean;

	/** Índices abiertos por defecto (modo no controlado) */
	readonly defaultOpen?: number[];

	/** Estado controlado: qué índices están abiertos */
	readonly openIndices?: number[];

	/** Callback de cambio de aperturas (modo controlado) */
	readonly onOpenChange?: (openIndices: number[]) => void;

	/** Clases opcionales */
	readonly className?: string;
	readonly itemClassName?: string;
	readonly triggerClassName?: string;
	readonly panelClassName?: string;
	readonly listClassName?: string;
	readonly emptyClassName?: string;

	/** Contenido cuando un grupo no tiene items */
	readonly renderEmptyGroup?: (group: TGroup, index: number) => React.ReactNode;
};

const classNameComponent = 'c-cnm-accordion';

export const Accordion = <TGroup, TItem>({
	groups,
	getGroupTitle,
	getItems,
	renderItem,
	getGroupKey,
	getItemKey,
	isMultiple = true,
	defaultOpen = [],
	openIndices,
	onOpenChange,
	className,
	itemClassName,
	triggerClassName,
	panelClassName,
	listClassName,
	emptyClassName,
	renderEmptyGroup,
}: AccordionProps<TGroup, TItem>) => {
	const rootId = useId();
	const wrapperClassName = classNames(classNameComponent, className);

	const isControlled = Array.isArray(openIndices);
	const [uncontrolledOpen, setUncontrolledOpen] = useState<IndexSet>(new Set(defaultOpen));

	const currentOpen: IndexSet = isControlled ? new Set(openIndices) : uncontrolledOpen;

	const setOpen = (next: IndexSet) => {
		const sorted = Array.from(next).sort((a, b) => a - b);
		if (isControlled) {
			onOpenChange?.(sorted);
		} else {
			setUncontrolledOpen(new Set(sorted));
		}
	};

	const toggle = (idx: number) => {
		// Partimos SIEMPRE del estado actual
		const base = new Set<number>(currentOpen);
		const isOpen = base.has(idx);

		if (isMultiple) {
			if (isOpen) base.delete(idx);
			else base.add(idx);
		} else {
			base.clear();
			if (!isOpen) base.add(idx);
		}

		setOpen(base);
	};

	if (!groups?.length) return <div className={wrapperClassName}>No hay contenido.</div>;

	return (
		<div className={wrapperClassName} role="region" aria-label="Acordeón">
			{groups.map((group, groupIndex) => {
				const isOpen = currentOpen.has(groupIndex);
				const buttonId = `${rootId}-btn-${groupIndex}`;
				const panelId = `${rootId}-panel-${groupIndex}`;
				const items = getItems(group, groupIndex) ?? [];
				const groupKey = getGroupKey?.(group, groupIndex) ?? groupIndex;

				return (
					<div key={groupKey} className={classNames(`${classNameComponent}__item`, itemClassName)}>
						<h3 className={`${classNameComponent}__header`}>
							<button
								id={buttonId}
								type="button"
								className={classNames(`${classNameComponent}__trigger`, triggerClassName)}
								aria-expanded={isOpen}
								aria-controls={panelId}
								onClick={() => toggle(groupIndex)}
							>
								<span className={`${classNameComponent}__title`}>
									{getGroupTitle(group, groupIndex)}
								</span>

								<span
									aria-hidden="true"
									className={classNames(
										`${classNameComponent}__icon`,
										isOpen && `${classNameComponent}__icon--open`,
									)}
								>
									{/* chevron simple (SVG) */}
									<svg viewBox="0 0 24 24" width="20" height="20">
										<path d="M6 9l6 6 6-6" fill="none" stroke="currentColor" strokeWidth="2" />
									</svg>
								</span>
							</button>
						</h3>

						<div
							id={panelId}
							role="region"
							aria-labelledby={buttonId}
							hidden={!isOpen}
							className={classNames(`${classNameComponent}__panel`, panelClassName)}
						>
							{items.length ? (
								<ul className={classNames(`${classNameComponent}__list`, listClassName)}>
									{items.map((item, itemIndex) => {
										const itemKey =
											getItemKey?.(item, itemIndex, group, groupIndex) ??
											`${groupKey}-${itemIndex}`;
										return (
											<li key={itemKey} className={`${classNameComponent}__list-item`}>
												{renderItem(item, itemIndex, group, groupIndex)}
											</li>
										);
									})}
								</ul>
							) : (
								<div className={classNames(`${classNameComponent}__empty`, emptyClassName)}>
									{renderEmptyGroup ? renderEmptyGroup(group, groupIndex) : 'Sin elementos.'}
								</div>
							)}
						</div>
					</div>
				);
			})}
		</div>
	);
};
